import type { ModelDefinition } from "@/models.js";

export const minimaxModels = [
	{
		id: "minimax-m3",
		name: "MiniMax M3",
		description:
			"MiniMax M3 is a multimodal foundation model with 512K token context, native multimodal understanding, and MiniMax Sparse Attention (MSA) for efficient long-context inference.",
		family: "minimax",
		releasedAt: new Date("2026-06-01"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-M3",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.12e-6",
				outputPrice: "2.4e-6",
				requestPrice: "0",
				contextSize: 512000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// MiniMax thinking is a binary toggle (`thinking.type`), not a
				// graduated effort: none/minimal disable it, low..max enable it.
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				splitTaggedReasoning: true,
				vision: true,
				tools: true,
				jsonOutput: true,
			},
			{
				providerId: "together-ai",
				externalId: "MiniMaxAI/MiniMax-M3",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 524288,
				maxOutput: 131072,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				// Together's deployment accepts any reasoning_effort string without
				// validating it, and no tier measurably changes the reasoning length,
				// so `none` — honoured through the `thinking` switch rather than
				// reasoning_effort — is the only effort this mapping really applies.
				reasoningEfforts: ["none"],
				requiresDisableThinkingParam: true,
				vision: false,
				tools: false,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "nebius",
				externalId: "MiniMaxAI/MiniMax-M3",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.3e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 1048576,
				maxOutput: undefined,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "tencent",
				externalId: "minimax-m3",
				// TokenHub bills M3 in two input-length bands; the mapping-level
				// prices mirror the first band so a request that never crosses
				// 512K is priced identically either way.
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "1.2e-6",
				pricingTiers: [
					{
						name: "Up to 512K",
						upToTokens: 512000,
						inputPrice: "0.3e-6",
						outputPrice: "1.2e-6",
						cachedInputPrice: "0.06e-6",
					},
					{
						name: "Over 512K",
						upToTokens: Infinity,
						inputPrice: "0.6e-6",
						outputPrice: "2.4e-6",
						cachedInputPrice: "0.12e-6",
					},
				],
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled on the M2/M3 family, so the "off"
				// tiers are not offered.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				// Reasoning comes back inline in the content as <think> tags
				// rather than in reasoning_content.
				splitTaggedReasoning: true,
				vision: true,
				tools: true,
				// response_format is accepted and then ignored — a json_object
				// request for a plain sentence comes back as prose.
				jsonOutput: false,
			},
		],
	},
	{
		id: "minimax-m2.7",
		name: "MiniMax M2.7",
		description:
			"MiniMax M2.7 with stronger reasoning and coding performance for complex tasks.",
		family: "minimax",
		releasedAt: new Date("2026-03-18"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-M2.7",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131100,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled for the M2.x family (`thinking.type:
				// "disabled"` is silently ignored upstream), so `none`/`minimal` are
				// not offered and thinking stays on at every tier.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				splitTaggedReasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "novita",
				externalId: "minimax/minimax-m2.7",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131100,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				// Together AI deprecates this serverless model on 2026-07-27,
				// recommending MiniMaxAI/MiniMax-M3 as the replacement.
				deactivatedAt: new Date("2026-07-27"),
				providerId: "together-ai",
				externalId: "MiniMaxAI/MiniMax-M2.7",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 196608,
				maxOutput: 196608,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: false,
				jsonOutput: true,
			},
			{
				providerId: "tencent",
				externalId: "minimax-m2.7",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 200000,
				maxOutput: 128000,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				splitTaggedReasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "minimax-m2.7-highspeed",
		name: "MiniMax M2.7 Highspeed",
		description:
			"Highspeed MiniMax M2.7 variant with the same model quality and lower latency.",
		family: "minimax",
		releasedAt: new Date("2026-03-18"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-M2.7-highspeed",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.06e-6",
				outputPrice: "2.4e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131100,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled for the M2.x family (`thinking.type:
				// "disabled"` is silently ignored upstream), so `none`/`minimal` are
				// not offered and thinking stays on at every tier.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				splitTaggedReasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "minimax-m2.5",
		name: "MiniMax M2.5",
		description: "MiniMax M2.5 with enhanced reasoning and tool support.",
		family: "minimax",
		releasedAt: new Date("2026-02-15"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-M2.5",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131100,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled for the M2.x family (`thinking.type:
				// "disabled"` is silently ignored upstream), so `none`/`minimal` are
				// not offered and thinking stays on at every tier.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				splitTaggedReasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "novita",
				externalId: "minimax/minimax-m2.5",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131100,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
			{
				deactivatedAt: new Date("2026-04-27"),
				providerId: "together-ai",
				externalId: "MiniMaxAI/MiniMax-M2.5",
				inputPrice: "0.3e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 228700,
				maxOutput: 131100,
				streaming: true,
				reasoning: true,
				vision: false,
				tools: false,
				jsonOutput: true,
			},
			{
				// Embercloud's upstream routing for this model is broken: streaming
				// returns finish_reason "error" with null content, and non-streaming
				// returns "Temporary routing error (400)". Deactivated until fixed.
				deactivatedAt: new Date("2026-06-03"),
				providerId: "embercloud",
				externalId: "minimax-m2.5",
				inputPrice: "0.2e-6",
				outputPrice: "1.2e-6",
				cachedInputPrice: "0.04e-6",
				requestPrice: "0",
				contextSize: 196608,
				maxOutput: 196608,
				streaming: true,
				reasoning: true,
				reasoningOutput: "omit",
				vision: false,
				tools: true,
				// Embercloud leaks <think> tags into JSON output, breaking json_object mode
				jsonOutput: false,
				supportedParameters: [
					"messages",
					"model",
					"stream",
					"stream_options",
					"temperature",
					"top_p",
					"max_tokens",
					"max_completion_tokens",
					"seed",
					"stop",
					"response_format",
					"tools",
					"tool_choice",
					"parallel_tool_calls",
				],
			},
			{
				providerId: "nebius",
				// Streaming tool calls and response_format: json_object are unreliable on Nebius
				stability: "unstable",
				externalId: "MiniMaxAI/MiniMax-M2.5",
				deactivatedAt: new Date("2026-08-31"),
				inputPrice: "0.3e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 196608,
				maxOutput: 131100,
				quantization: "fp4",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
			},
		],
	},
	{
		id: "minimax-m2.5-highspeed",
		name: "MiniMax M2.5 Highspeed",
		description:
			"Highspeed MiniMax M2.5 variant with the same model quality and lower latency.",
		family: "minimax",
		releasedAt: new Date("2026-02-15"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-M2.5-highspeed",
				inputPrice: "0.6e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "2.4e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131100,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled for the M2.x family (`thinking.type:
				// "disabled"` is silently ignored upstream), so `none`/`minimal` are
				// not offered and thinking stays on at every tier.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				splitTaggedReasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "minimax-m2",
		name: "MiniMax M2",
		description: "MiniMax M2 model with reasoning and tool support.",
		family: "minimax",
		releasedAt: new Date("2025-10-27"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-M2",
				inputPrice: "0.2e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "1.0e-6",
				requestPrice: "0",
				contextSize: 196608,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled for the M2.x family (`thinking.type:
				// "disabled"` is silently ignored upstream), so `none`/`minimal` are
				// not offered and thinking stays on at every tier.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				splitTaggedReasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "minimax-m2.1",
		name: "MiniMax M2.1",
		description:
			"MiniMax M2.1 is a 10B-activated open-source coding and agent model with function calling, structured output, and reasoning support.",
		family: "minimax",
		releasedAt: new Date("2025-12-23"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-M2.1",
				inputPrice: "0.27e-6",
				outputPrice: "1.1e-6",
				requestPrice: "0",
				contextSize: 196608,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled for the M2.x family (`thinking.type:
				// "disabled"` is silently ignored upstream), so `none`/`minimal` are
				// not offered and thinking stays on at every tier.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				splitTaggedReasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
			{
				providerId: "novita",
				test: "skip",
				externalId: "minimax/minimax-m2.1",
				inputPrice: "0.3e-6",
				cachedInputPrice: "0.03e-6",
				outputPrice: "1.2e-6",
				requestPrice: "0",
				contextSize: 204800,
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: false,
			},
		],
	},
	{
		id: "minimax-m2.1-lightning",
		name: "MiniMax M2.1 Lightning",
		description:
			"MiniMax M2.1 Lightning is a faster variant of M2.1 with lower latency for coding tasks.",
		family: "minimax",
		releasedAt: new Date("2025-12-23"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-M2.1-lightning",
				inputPrice: "0.12e-6",
				outputPrice: "0.48e-6",
				requestPrice: "0",
				contextSize: 196608,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// Thinking cannot be disabled for the M2.x family (`thinking.type:
				// "disabled"` is silently ignored upstream), so `none`/`minimal` are
				// not offered and thinking stays on at every tier.
				reasoningEfforts: ["low", "medium", "high", "xhigh", "max"],
				splitTaggedReasoning: true,
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "minimax-text-01",
		name: "MiniMax Text 01",
		description:
			"MiniMax-Text-01 is a 456B parameter model with 4M token context using Lightning Attention.",
		family: "minimax",
		releasedAt: new Date("2025-01-15"),
		providers: [
			{
				providerId: "minimax",
				externalId: "MiniMax-Text-01",
				inputPrice: "0.2e-6",
				outputPrice: "1.1e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				splitTaggedReasoning: true,
				reasoningOutput: "omit",
				vision: false,
				tools: true,
				jsonOutput: false,
			},
		],
	},
	{
		id: "minimax-hailuo-2-3",
		name: "MiniMax Hailuo 2.3",
		description:
			"MiniMax's video generation model supporting text-to-video and image-to-video up to 10 seconds at 768p or 1080p resolution.",
		family: "minimax",
		output: ["video"],
		releasedAt: new Date("2025-11-07"),
		providers: [
			{
				test: "skip",
				providerId: "minimax",
				externalId: "MiniMax-Hailuo-2.3",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				perSecondPrice: {
					"720p": "0.0467",
					"768p": "0.0467",
					"1080p": "0.0817",
				},
				contextSize: 2000,
				maxOutput: 1,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				videoGenerations: true,
				supportedVideoSizes: ["1280x720", "1366x768", "1920x1080"],
				supportedVideoDurationsSeconds: [6, 10],
				supportedVideoDurationsSecondsImageToVideo: [6, 10],
				supportsVideoAudio: false,
				supportsVideoWithoutAudio: true,
			},
		],
	},
	{
		id: "minimax-h3-max",
		name: "MiniMax H3 Max",
		description:
			"MiniMax's fast H3 video model with native synchronized audio, supporting text-to-video and first/last-frame image-to-video from 5 to 15 seconds at 480p or 768p.",
		family: "minimax",
		output: ["video"],
		maxVideoDurationSeconds: 15,
		releasedAt: new Date("2026-09-08"),
		providers: [
			{
				test: "skip",
				providerId: "minimax",
				externalId: "MiniMax-H3-Max",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				perSecondPrice: {
					"480p": "0.05",
					"768p": "0.08",
				},
				contextSize: 7000,
				maxOutput: 1,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				videoGenerations: true,
				supportedVideoSizes: [
					"848x480",
					"854x480",
					"480x854",
					"1366x768",
					"768x1366",
				],
				supportedVideoDurationsSeconds: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
] as const satisfies ModelDefinition[];
